/**
 * @module arcgisElevationService
 *
 * ArcGIS Elevation API client. Returns ground elevation in HAE (height above
 * ellipsoid) via `relativeTo=ellipsoid`. Used for ESRI map elevation; routing
 * and fallback are handled by whichElevationService.
 */
import { getEsriApiKey } from '@rsos/utils/metaTags';

const ARCGIS_ELEVATION_URL =
  'https://elevation-api.arcgis.com/arcgis/rest/services/elevation-service/v1/elevation/at-point';

const elevationCache = new Map<string, number>();

/**
 * Get ground elevation (HAE) from ArcGIS Elevation API only.
 * No fallback; use whichElevationService for map-based routing and fallback.
 *
 * @param latitude - Location latitude
 * @param longitude - Location longitude
 * @returns Ground elevation in meters (HAE), or null on failure
 */
export async function getGroundElevationArcGIS(
  latitude: number,
  longitude: number,
): Promise<number | null> {
  const token = getEsriApiKey();
  if (!token) {
    return null;
  }

  const key = `${latitude.toFixed(6)},${longitude.toFixed(6)}`;
  if (elevationCache.has(key)) {
    return elevationCache.get(key) ?? null;
  }

  try {
    const url = new URL(ARCGIS_ELEVATION_URL);
    url.searchParams.set('lon', longitude.toString());
    url.searchParams.set('lat', latitude.toString());
    url.searchParams.set('relativeTo', 'ellipsoid');
    url.searchParams.set('token', token);
    url.searchParams.set('f', 'json');

    const response = await fetch(url.toString());
    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (data?.error) {
      // eslint-disable-next-line no-console
      console.warn('[ArcGIS Elevation]', data.error.message);
      return null;
    }

    const elevation = data?.result?.elevation;
    if (typeof elevation === 'number' && !isNaN(elevation)) {
      elevationCache.set(key, elevation);
      return elevation;
    }

    return null;
  } catch {
    return null;
  }
}
